import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ApiResponse,
  AssessmentConfigDetailsResponse,
  DeedAssignmentDetailsResponse,
  InitiatePaymentResponse,
  OccupancyDetailsResponse,
  PlotDetailsResponse,
  PowerOfAttorneyDetailsResponse,
} from '../../../core/models/api-response.model';
import { AsinDetails } from '../../../core/models/asin.model';
import { TccDetails } from '../../../core/models/tcc.model';
import { InvoiceGenerationRequest } from '../../../core/models/api-request.model';
import { AssessmentTypes } from '../../../core/models/assessment.model';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiUrl;

  private options(params?: Record<any, any>) {
    return {
      ...params,
      withCredentials: true,
    };
  }

  get<T>(
    path: string,
    params?: Parameters<typeof this.http.get>[1],
  ): Observable<T> {
    return this.http.get<T>(
      `${this.base}/${path.replace(/^\//g, '')}`,
      this.options(params),
    );
  }

  post<T>(
    path: string,
    body: unknown,
    params?: Parameters<typeof this.http.post>[2],
  ): Observable<T> {
    return this.http.post<T>(
      `${this.base}/${path.replace(/^\//g, '')}`,
      body,
      this.options(params),
    );
  }

  put<T>(
    path: string,
    body: unknown,
    params?: Parameters<typeof this.http.put>[2],
  ): Observable<T> {
    return this.http.put<T>(
      `${this.base}/${path.replace(/^\//g, '')}`,
      body,
      this.options(params),
    );
  }

  patch<T>(
    path: string,
    body: unknown,
    params?: Parameters<typeof this.http.patch>[2],
  ): Observable<T> {
    return this.http.patch<T>(
      `${this.base}/${path.replace(/^\//g, '')}`,
      body,
      this.options(),
    );
  }

  delete<T>(
    path: Parameters<typeof this.http.delete>[0],
    options?: Parameters<typeof this.http.delete>[1],
  ): Observable<T> {
    return this.http.delete<T>(`${this.base}/${path.replace(/^\//g, '')}`, {
      ...options,
      withCredentials: true,
    });
  }

  getErrorMessage(error: unknown): string {
    let errorMessage: string = '';
    if (error === null || error === undefined) return errorMessage;
    if (typeof error === 'string') {
      errorMessage = error;
    }
    if (error instanceof HttpErrorResponse) {
      errorMessage = error.message;
    }
    if (typeof error === 'object' && 'message' in error) {
      // errorMessage = JSON.stringify(error.message);
      errorMessage = error.message as string;
    }
    return errorMessage;
  }

  refreshToken(payload: any): Observable<ApiResponse> {
    return this.post('/auth/refresh', payload);
  }

  validateAsin(
    asin: string,
    tin?: string,
  ): Observable<ApiResponse<AsinDetails>> {
    const params: HttpParams = new HttpParams({
      fromObject: {
        asin: asin,
        tin: tin ?? asin,
      },
    });

    return this.get<ApiResponse<AsinDetails>>(
      '/utilities/validate-asin/details',
      {
        params: params,
      },
    );
  }

  validateTcc(tcc: string): Observable<ApiResponse<TccDetails>> {
    const params: HttpParams = new HttpParams({
      fromObject: {
        tcc_number: tcc,
      },
    });

    return this.get<ApiResponse<TccDetails>>('/utilities/validate-tcc', {
      params: params,
    });
  }

  validateFileNumber(
    fileNumber: string,
  ): Observable<ApiResponse<PlotDetailsResponse>> {
    return this.get<ApiResponse<PlotDetailsResponse>>(
      `/plots/validate-file-number/${fileNumber}`,
    );
  }

  getFileName(response: HttpResponse<Blob>): string | null {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (!contentDisposition) return null;

    // RFC 5987 / RFC 6266 filename*
    const filenameStar = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

    if (filenameStar?.[1]) {
      return decodeURIComponent(filenameStar[1]);
    }

    // Traditional filename
    const filename = contentDisposition.match(/filename="?([^"]+)"?/i);
    return filename?.[1] || null;
  }

  fetchStates(): Observable<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('utilities/states').pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to fetch states');
        else return of(res);
      }),
    );
  }

  fetchLgas(stateId: string): Observable<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>(
      `utilities/states/${stateId}/lgas`,
    ).pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to fetch lgas');
        else return of(res);
      }),
    );
  }

  fetchDistricts(): Observable<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('utilities/districts').pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to fetch districts');
        else return of(res);
      }),
    );
  }

  fetchLandSchedule(): Observable<ApiResponse<any[]>> {
    return this.get<ApiResponse<any[]>>('utilities/land-schedules').pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(
            () => res.message || 'Failed to fetch land schedules',
          );
        else return of(res);
      }),
    );
  }

  generateInvoice(
    payload: InvoiceGenerationRequest,
  ): Observable<ApiResponse<InitiatePaymentResponse>> {
    const url = '/payments/generate-invoice';
    return this.post<ApiResponse<InitiatePaymentResponse>>(url, payload).pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(
            () => res.message || 'Failed to generate invoice and make payment',
          );
        else return of(res);
      }),
    );
  }

  makePayment(
    assessmentId: string,
  ): Observable<ApiResponse<InitiatePaymentResponse>> {
    const url = '/payments/pay-invoice';
    const payload = {
      assessmentId: assessmentId,
    };

    return this.post<ApiResponse<InitiatePaymentResponse>>(url, payload).pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to make payment');
        else return of(res);
      }),
    );
  }

  reconcilePayment(
    assessmentId: string,
  ): Observable<ApiResponse<PaymentResponse>> {
    const url = `/payments/reconcile/${assessmentId}`;

    return this.get<ApiResponse<PaymentResponse>>(url).pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to reconcile payment');
        else return of(res);
      }),
    );
  }

  fetchAssessmentDetails(assessmentType: AssessmentTypes) {
    const url = `assessment-configs/type/${assessmentType}`;
    return this.get<ApiResponse<AssessmentConfigDetailsResponse[]>>(url).pipe(
      switchMap((res) => {
        if (!res.success)
          return throwError(() => res.message || 'Failed to fetch assessments');
        else return of(res);
      }),
    );
  }

  fetchOccupancyApplication(
    applicationId: string,
  ): Observable<ApiResponse<OccupancyDetailsResponse>> {
    return this.get<ApiResponse<OccupancyDetailsResponse>>(
      `occupancy/${applicationId}`,
    );
  }

  fetchAttorneyApplication(
    applicationId: string,
  ): Observable<ApiResponse<PowerOfAttorneyDetailsResponse>> {
    return this.get<ApiResponse<PowerOfAttorneyDetailsResponse>>(
      `power-of-attorney/${applicationId}`,
    );
  }

  fetchDeedsOfAssignmentApplication(
    applicationId: string,
  ): Observable<ApiResponse<DeedAssignmentDetailsResponse>> {
    return this.get<ApiResponse<DeedAssignmentDetailsResponse>>(
      `deed-assignment/${applicationId}`,
    );
  }

  fetchDemandNotice(assessmentId: any): Observable<HttpResponse<Blob>> {
    const url = `/payments/notice`;
    const params = new HttpParams({
      fromObject: {
        id: assessmentId,
      },
    });

    return this.get<HttpResponse<Blob>>(url, {
      params,
      responseType: 'blob' as any,
      observe: 'response' as any,
    });
  }

  fetchPaymentReceipt(assessmentId: any): Observable<HttpResponse<Blob>> {
    const url = `/payments/receipt`;
    const params = new HttpParams({
      fromObject: {
        id: assessmentId,
      },
    });

    return this.get<HttpResponse<Blob>>(url, {
      params,
      responseType: 'blob' as any,
      observe: 'response' as any,
    });
  }
}
